import React, { useState, useEffect, useCallback } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { Calendar, Eye, Tag, FileText, Download, Upload, Link2 } from "lucide-react";
import VideoComments from "./VideoComments";
import UploadMaterialModal from "../Materials/UploadMaterialModal";
import MapMaterialModal from "../Materials/MapMaterialModal";
import { AppButton, AppBadge } from "../ui";
import apiClient from "../../utils/apiClient";
import ProtectedVideoPlayer from "../Common/ProtectedVideoPlayer";
import { usePlan } from "../../contexts/PlanContext";
import { getCurrentUser } from "../../utils/auth";
import { isDesktopApp } from "../../utils/desktopApp";
import DesktopAppRequired from "../Common/DesktopAppRequired";
import useMaterialViewer, { isViewableType } from "../Materials/useMaterialViewer";
import { canDownloadFile } from "../ContentProtection/contentAccess";
import "./Videos.scss";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const CATEGORY_LABELS = { ppt: "PPT", script: "Script", notes: "Notes", other: "Other" };
const CATEGORY_COLORS = { ppt: "amber", script: "blue", notes: "emerald", other: "zinc" };

const VideoPlayerModal = ({ video, show, onClose, role, batchId, onDownload, downloading = false }) => {
  const { hasPlanFeature } = usePlan();
  const currentUser = getCurrentUser();

  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [materials, setMaterials] = useState([]);
  const [linkedMaterials, setLinkedMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [courseVideoSecurity, setCourseVideoSecurity] = useState(false);

  // Desktop app required only when plan+institute enabled AND course has videoSecurity on
  const requireDesktopApp = hasPlanFeature('videoSecurityEnabled') && courseVideoSecurity && !isDesktopApp() && currentUser?.usertype === 'student';

  const isAdmin = role === "admin" || role === "superadmin";
  const isInstructor = role === "instructor";
  const canUpload = isAdmin || isInstructor;
  // Download is admin/superadmin only — students/instructors stream inline only.
  const canDownloadVideo = !!onDownload && isAdmin && video?.status === "ready";

  // Fetch signed URL. Also the player's `onExpired`: on a refreshed link the
  // player puts the playhead back itself, and a failed refresh leaves the old
  // link in place for its "Try again".
  const fetchSignedUrl = useCallback(async ({ refresh = false } = {}) => {
    if (!video?._id) return;
    try {
      const { data } = await apiClient.get(`/videos/${video._id}/stream`);
      if (data.success) {
        setSignedUrl(data.data.url);
        if (data.data.videoSecurity !== undefined) setCourseVideoSecurity(!!data.data.videoSecurity);
        setError("");
      } else if (!refresh) {
        setError("Unable to load video.");
      }
    } catch {
      if (!refresh) setError("Unable to load video.");
    }
  }, [video?._id]);
  const refreshSignedUrl = useCallback(() => fetchSignedUrl({ refresh: true }), [fetchSignedUrl]);

  useEffect(() => {
    if (show && video?._id) {
      setSignedUrl(null);
      setError("");
      setLoading(true);
      fetchSignedUrl().finally(() => setLoading(false));
    }
  }, [show, video?._id, fetchSignedUrl]);

  // Fetch video-level materials (uploaded directly)
  const fetchMaterials = useCallback(async () => {
    if (!video?._id) return;
    setMaterialsLoading(true);
    try {
      const { data } = await apiClient.get(`/materials/video/${video._id}`);
      if (data.success) setMaterials(data.data || []);
    } catch {
      setMaterials([]);
    }
    setMaterialsLoading(false);
  }, [video?._id]);

  // Fetch linked batch materials
  const fetchLinkedMaterials = useCallback(async () => {
    const bid = batchId || video?.batch?._id || video?.batch;
    if (!video?._id || !bid) return;
    try {
      const { data } = await apiClient.get(`/materials/batch/${bid}/by-video/${video._id}`);
      setLinkedMaterials(data.data || []);
    } catch {
      setLinkedMaterials([]);
    }
  }, [video?._id, batchId, video?.batch]);

  useEffect(() => {
    if (show && video?._id) {
      fetchMaterials();
      fetchLinkedMaterials();
    }
  }, [show, video?._id, fetchMaterials, fetchLinkedMaterials]);

  // Documents open in the in-app viewer so their content protection applies;
  // only a downloadable, non-viewable type (an archive) downloads.
  const { openMaterial, downloadMaterial, viewer: materialViewer } = useMaterialViewer();
  const handleOpenMaterial = (mat) =>
    (isViewableType(mat.fileType) || !canDownloadFile(mat) ? openMaterial(mat) : downloadMaterial(mat));

  const handleClose = () => {
    setSignedUrl(null);
    setError("");
    setMaterials([]);
    setLinkedMaterials([]);
    onClose();
  };

  const handleLinkExisting = async (materialId) => {
    await apiClient.put(`/materials/${materialId}/link-video`, { videoId: video._id });
    setShowLinkModal(false);
    fetchLinkedMaterials();
  };

  // Combine uploaded + linked materials for display
  const allDocuments = [
    ...materials.filter(m => m.status === "ready"),
    ...linkedMaterials.map(m => ({ ...m, _isLinked: true })),
  ];
  const linkedIds = linkedMaterials.map(m => String(m._id));

  if (!video) return null;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      className="video-player-modal"
      dialogClassName="modal-90w"
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-6 fw-semibold">{video.title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Desktop App Guard — students on secureVideoPlayer plans */}
        {requireDesktopApp ? (
          <DesktopAppRequired />
        ) : (
        <>
        {/* Video Player */}
        <div className="video-player-modal__video-wrapper">
          {loading && (
            <div className="videos-spinner">
              <Spinner animation="border" />
            </div>
          )}

          {error && (
            <div className="text-center text-muted p-4">{error}</div>
          )}

          {signedUrl && !loading && (
            <ProtectedVideoPlayer
              src={signedUrl}
              title={video.title}
              autoPlay
              preload="auto"
              onExpired={refreshSignedUrl}
            />
          )}
        </div>
        </>
        )}

        {/* Video Info */}
        <div className="video-player-modal__info">
          <div className="d-flex align-items-start justify-content-between gap-2">
            <h5 className="video-player-modal__title">{video.title}</h5>
            {canDownloadVideo && (
              <AppButton
                variant="outline"
                size="sm"
                onClick={() => !downloading && onDownload(video)}
                disabled={downloading}
                style={{ fontSize: "0.75rem", padding: "2px 8px", flexShrink: 0 }}
                title="Download this recording"
              >
                <Download size={12} /> {downloading ? "Preparing…" : "Download"}
              </AppButton>
            )}
          </div>
          <div className="video-player-modal__meta">
            <span>
              <Calendar size={13} />
              {formatDate(video.recordedAt)}
            </span>
            <span>
              <Eye size={13} />
              {video.viewCount || 0} views
            </span>
            {video.topic && (
              <span>
                <Tag size={13} />
                {video.topic}
              </span>
            )}
          </div>
          {video.description && (
            <p className="mt-2 mb-0 text-muted" style={{ fontSize: "0.8125rem" }}>
              {video.description}
            </p>
          )}
        </div>

        {/* Video-level Documents */}
        <div className="video-player-modal__materials p-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h6 className="mb-0 fw-semibold d-flex align-items-center gap-1" style={{ fontSize: "0.875rem" }}>
              <FileText size={15} />
              Documents
              {allDocuments.length > 0 && (
                <span className="badge bg-secondary bg-opacity-25 text-secondary ms-1" style={{ fontSize: "0.7rem" }}>
                  {allDocuments.length}
                </span>
              )}
            </h6>
            {canUpload && (
              <div className="d-flex align-items-center gap-1">
                <AppButton variant="outline" size="sm" onClick={() => setShowLinkModal(true)} style={{ fontSize: "0.75rem", padding: "2px 8px" }}>
                  <Link2 size={12} /> Link Existing
                </AppButton>
                <AppButton variant="outline" size="sm" onClick={() => setShowUpload(true)} style={{ fontSize: "0.75rem", padding: "2px 8px" }}>
                  <Upload size={12} /> Upload New
                </AppButton>
              </div>
            )}
          </div>

          {materialsLoading ? (
            <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
          ) : allDocuments.length === 0 ? (
            <p className="text-muted small mb-0">No documents attached to this video.</p>
          ) : (
            <div className="video-materials-list">
              {allDocuments.map((mat) => (
                <div
                  key={mat._id}
                  className="video-material-item d-flex align-items-center gap-2 py-1 px-2 rounded"
                  style={{ cursor: "pointer", background: "var(--bg-secondary, #f8f9fa)", marginBottom: 4 }}
                  onClick={() => handleOpenMaterial(mat)}
                >
                  <FileText size={14} style={{ color: mat._isLinked ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))", flexShrink: 0 }} />
                  <div className="flex-grow-1 text-truncate" style={{ fontSize: "0.8125rem" }}>
                    <span className="fw-medium">{mat.title}</span>
                    {mat.fileSize > 0 && (
                      <span className="text-muted ms-2" style={{ fontSize: "0.7rem" }}>
                        {formatSize(mat.fileSize)}
                      </span>
                    )}
                  </div>
                  {mat._isLinked && (
                    <span className="badge" style={{ fontSize: "0.6rem", background: "hsl(var(--primary) / 0.1)", color: "hsl(var(--primary))" }}>Linked</span>
                  )}
                  <AppBadge variant="outline" color={CATEGORY_COLORS[mat.category] || "zinc"} style={{ fontSize: "0.65rem" }}>
                    {CATEGORY_LABELS[mat.category] || mat.category}
                  </AppBadge>
                  {isViewableType(mat.fileType) || !canDownloadFile(mat)
                    ? <Eye size={13} className="text-muted" style={{ flexShrink: 0 }} />
                    : <Download size={13} className="text-muted" style={{ flexShrink: 0 }} />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comments */}
        <VideoComments videoId={video._id} role={role} />
        {materialViewer}
      </Modal.Body>

      {/* Upload modal for video-level materials */}
      <UploadMaterialModal
        show={showUpload}
        onClose={() => setShowUpload(false)}
        batchId={video.batch?._id || video.batch}
        videoId={video._id}
        defaultLevel="video"
        hideLevel
        onSuccess={() => { setShowUpload(false); fetchMaterials(); }}
      />

      {/* Link existing batch material modal */}
      <MapMaterialModal
        show={showLinkModal}
        batchId={batchId || video.batch?._id || video.batch}
        targetLabel={video.title}
        alreadyLinkedIds={linkedIds}
        onLink={handleLinkExisting}
        onClose={() => setShowLinkModal(false)}
      />
    </Modal>
  );
};

export default VideoPlayerModal;
